// frontend/app/components/lessonPlans/lessonQuizSource.js
//
// Fetches the questions behind one `saras_topic_quiz` block.
//
// Extracted from `LessonQuizPlayer` for the same reason `lessonVideoSource`
// was extracted from `LessonVideoPlayer`: the failure modes are the
// interesting part, and they are worth testing without mounting a component.
//
// The runtime payload carries only a question *count* for a quiz block — a
// lesson with four quizzes would otherwise ship a hundred questions and their
// answer keys just to open the cover page. They are fetched when the student
// opens that one block.

import axios from "axios";

import { FASTAPI_BASE_URL } from "../../config";

export class LessonQuizSourceError extends Error {}

/**
 * `{ questions, questionCount, totalQuestionCount, stepKey, conceptKey, ... }`
 * for one block, or a `LessonQuizSourceError` a player can put on screen.
 *
 * A quiz with no questions raises rather than resolving empty: the backend
 * refuses to substitute a neighbouring topic's bank, so "nothing here" is a
 * real answer the student needs to see — and be able to close.
 */
export async function loadLessonBlockQuiz({
  lessonPlanId,
  blockId,
  stepKey = null,
  languageId = 1,
  authToken,
}) {
  if (!lessonPlanId || !blockId) {
    throw new LessonQuizSourceError("This lesson block has no quiz reference.");
  }

  const params = { language_id: languageId };
  if (stepKey) params.step_key = stepKey;

  let response;
  try {
    response = await axios.get(
      `${FASTAPI_BASE_URL}/lesson-plans/${lessonPlanId}/blocks/${encodeURIComponent(
        blockId
      )}/quiz`,
      {
        params,
        headers: authToken ? { Authorization: `Bearer ${authToken}` } : undefined,
      }
    );
  } catch (error) {
    // The server's own reason where there is one. "Could not load the quiz" is
    // true of a 404 and of a 500 alike, and tells whoever is looking at the
    // screen nothing about which.
    const detail = error?.response?.data?.detail;
    throw new LessonQuizSourceError(
      (typeof detail === "string" ? detail : detail?.message) ||
        error?.message ||
        "Could not load this quiz."
    );
  }

  const data = response?.data || {};
  const questions = Array.isArray(data.questions) ? data.questions : [];

  if (questions.length === 0) {
    throw new LessonQuizSourceError(
      "This topic has no quiz questions in the current language."
    );
  }

  return {
    questions,
    stepKey: data.step_key || stepKey || null,
    conceptKey: data.concept_key ?? null,
    topicSubtopicId: data.topic_subtopic_id ?? null,
    questionCount: questions.length,
    // What the bank holds, which an authored `max_questions` makes differ from
    // what this block serves.
    totalQuestionCount: data.total_question_count ?? questions.length,
  };
}
